import { CoverageProgressBar } from '@client/components/ltc-financial-planning/CoverageProgressBar';
import { MetricCard } from '@client/components/ltc-financial-planning/MetricCard';
import { useCoverageAssessmentData } from '@client/hooks/useCoverageAssessmentData';
import { cn } from '@client/lib';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { StepContentProps } from '../types';

// Get contextual message based on coverage percentage
const getCoverageMessage = (coveragePercentage: number) => {
  if (coveragePercentage >= 100) {
    return {
      title: 'Fully Covered',
      message:
        "Your projected assets can handle the estimated long-term care costs. You're in a strong position.",
    };
  } else if (coveragePercentage >= 75) {
    return {
      title: 'Nearly There',
      message: "You're close to full coverage. A small adjustment could close the remaining gap.",
    };
  } else if (coveragePercentage >= 50) {
    return {
      title: 'Good Progress',
      message:
        "You have solid coverage building. Let's explore options to strengthen your plan further.",
    };
  } else if (coveragePercentage >= 25) {
    return {
      title: 'Moderate Coverage',
      message:
        "There's a notable gap between your assets and projected costs. We'll show you strategies to bridge it.",
    };
  } else {
    return {
      title: 'Significant Gap',
      message:
        "Your current assets cover a small portion of projected costs. Don't worry—we'll explore funding options together.",
    };
  }
};

export function CoverageAssessmentStep(props: StepContentProps) {
  const {
    yearsTillLtc,
    allPhaseInflatedProfessionalShareCost,
    multipleSelfFundingProjectedCostCoverage,
    coverageGap,
    selfFundingSourceCount,
  } = useCoverageAssessmentData();

  const [showBreakdown, setShowBreakdown] = useState(false);

  const coveragePercentage =
    allPhaseInflatedProfessionalShareCost > 0
      ? (multipleSelfFundingProjectedCostCoverage / allPhaseInflatedProfessionalShareCost) * 100
      : 0;
  const coverageMessage = getCoverageMessage(coveragePercentage);

  return (
    <div className="mx-auto max-w-3xl py-8">
      {/* Title Section */}
      <div className="mb-10 text-center">
        <h2 className="mb-3 text-2xl font-bold text-gray-900 md:text-3xl">
          Your Coverage Assessment
        </h2>
        <p className="mx-auto max-w-xl text-gray-700">
          Based on your accounts, here's how your projected assets compare to estimated long-term
          care costs.
        </p>
      </div>

      {/* Metric Cards */}
      <div className="mb-6 grid gap-4 md:grid-cols-2">
        <MetricCard
          title="Self-Funding Capacity"
          amount={multipleSelfFundingProjectedCostCoverage}
          subtitle={`${yearsTillLtc}-year projection based on your assets`}
          className="text-primary"
        />
        <MetricCard
          title="Estimated LTC Cost"
          amount={allPhaseInflatedProfessionalShareCost}
          subtitle="Projected market rates in your locale"
          className="text-gray-900"
        />
      </div>

      {/* Hero Progress Bar with Contextual Message */}
      <div className="mb-6 rounded-2xl border bg-white p-6 md:p-8">
        {/* Contextual message above progress bar */}
        <div className="mb-8">
          <h3 className="mb-1 text-lg font-semibold text-gray-900">{coverageMessage.title}</h3>
          <p className="max-w-md text-sm text-gray-700">{coverageMessage.message}</p>
        </div>

        <CoverageProgressBar
          coveredAmount={multipleSelfFundingProjectedCostCoverage}
          gapAmount={coverageGap}
          totalCost={allPhaseInflatedProfessionalShareCost}
        />
      </div>

      {/* Calculation Breakdown (Collapsible) */}
      <div className="mb-8 overflow-hidden rounded-xl border border-gray-300 bg-white">
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="flex w-full items-center justify-between px-5 py-4 text-left transition-colors hover:bg-gray-50"
        >
          <span className="text-sm font-medium text-gray-900">How we calculated this</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-gray-700 transition-transform duration-200',
              showBreakdown && 'rotate-180',
            )}
          />
        </button>

        {showBreakdown && (
          <div className="border-t border-gray-300 px-5 pb-5 pt-4">
            <ul className="list-inside list-disc space-y-2.5 text-sm text-gray-700">
              <li>
                Projected your {selfFundingSourceCount} account(s) forward {yearsTillLtc} years
                using your expected returns
              </li>
              <li>Applied gains taxes to your investment growth</li>
              <li>Included compounded monthly growth on future contributions</li>
              <li>Applied your LTC allocation percentage to each account</li>
              <li>Compared against your estimated long-term care costs</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
